import { useMemo, useState, type CSSProperties } from 'react'
import { ArrowLeft, Check, Loader2, Pencil, Plus, Sparkles } from 'lucide-react'
import { parseTemplate, type BusinessChoice, type Idea } from '@/lib/discovery'
import type { DiscoveryBlanks } from '@/types'

// ---------------------------------------------------------------------------
// Fill in the blanks — an idea turned into one plain sentence whose parts are
// tappable: the business, the idea, who uses it, what needs sign-off, where it
// is used. The sentence and its choices are DATA (catalogue `blanks`); the
// business and idea slots are filled from the user's own choices. Below it,
// "Anything else?" takes the rest in the user's own words, with quick-add
// chips from the idea's deliverables and the catalogue's extras. The result
// is ordinary description text: Build it, or carry it into the questions.
// ---------------------------------------------------------------------------

interface Props {
  blanks: DiscoveryBlanks
  idea: Idea
  /** Other ideas the idea slot can switch to (same area or kind). */
  relatedIdeas: Idea[]
  business: BusinessChoice | null
  building: boolean
  onOpenBusiness: () => void
  onChangeIdea: (idea: Idea) => void
  onBack: () => void
  /** Build straight from the finished text. */
  onBuild: (text: string) => void
  /** The sentence and the "anything else" notes, into the questions. */
  onQuestions: (sentence: string, notes: string) => void
  /** Hand the text back to the start page's box for free editing. */
  onFreeText: (text: string) => void
}

/** "Purchase requisition to PO" → "purchase requisition to PO" (keeps acronyms). */
const lcFirst = (s: string) => (s.length > 1 && s[1] === s[1].toLowerCase() ? s[0].toLowerCase() + s.slice(1) : s)
const ucFirst = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

export default function FillInBlanks({
  blanks, idea, relatedIdeas, business, building,
  onOpenBusiness, onChangeIdea, onBack, onBuild, onQuestions, onFreeText,
}: Props) {
  const parts = useMemo(() => parseTemplate(blanks.template), [blanks.template])
  const optionSlots = useMemo(
    () => parts.flatMap((p) => ('slot' in p && blanks.slots[p.slot]?.options?.length ? [p.slot] : [])),
    [parts, blanks.slots],
  )
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(
    optionSlots.map((s) => [s, blanks.slots[s].options?.[0] || '']),
  ))
  const [active, setActive] = useState<string>(optionSlots[0] || 'idea')
  const [own, setOwn] = useState('')
  const [extra, setExtra] = useState('')

  const valueOf = (slot: string): string => {
    const def = blanks.slots[slot]
    if (def?.source === 'industry') return business ? `our ${business.name.toLowerCase()} business` : (def.default || 'our business')
    if (def?.source === 'idea') return lcFirst(idea.title)
    return values[slot] || def?.default || ''
  }

  // A typed value before a new sentence gets its full stop.
  const sentence = parts.map((p, i) => {
    if ('text' in p) return p.text
    const v = valueOf(p.slot)
    const next = parts[i + 1]
    const needsStop = next && 'text' in next && /^\s+[A-Z]/.test(next.text) && !/[.!?]$/.test(v)
    return needsStop ? `${v}.` : v
  }).join('').replace(/\s{2,}/g, ' ').trim()
  const fullText = extra.trim() ? `${sentence}\n\n${extra.trim()}` : sentence

  const quickAdds = [
    ...idea.whatYouGet.map((w) => ({ label: w, text: `Include ${lcFirst(w)}.` })),
    ...blanks.extras.map((e) => ({ label: e.replace(/[.;].*$/, ''), text: e })),
  ]
  const toggleQuick = (text: string) => {
    setExtra((cur) => (cur.includes(text)
      ? cur.replace(text, '').replace(/[ \t]{2,}/g, ' ').trim()
      : `${cur.trim() ? `${cur.trim()} ` : ''}${text}`))
  }

  const activeDef = blanks.slots[active]
  const choose = (v: string) => setValues((prev) => ({ ...prev, [active]: v }))
  const applyOwn = () => {
    const v = own.trim()
    if (!v || activeDef?.source) return
    choose(v)
    setOwn('')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button type="button" onClick={onBack} disabled={building} style={backLink}><ArrowLeft size={14} /> All ideas</button>
      <div>
        <span style={eyebrow}>{idea.title}{business ? ` · ${business.name}` : ''}</span>
        <h1 style={{ margin: '10px 0 0', fontSize: '1.9625rem', lineHeight: 1.2, fontWeight: 600 }}>Fill in the blanks</h1>
        <p style={{ margin: '6px 0 0', color: 'var(--color-text-secondary)' }}>
          Tap any highlighted part to change it. Everything else you can leave as it is.
        </p>
      </div>

      <div style={cardBox}>
        <p style={{ margin: 0, padding: '24px 24px 16px', fontSize: '1.3625rem', lineHeight: 2 }}>
          {parts.map((p, i) => {
            if ('text' in p) return <span key={i}>{p.text}</span>
            const def = blanks.slots[p.slot]
            if (!def) return null
            const isActive = active === p.slot && def.source !== 'industry'
            return (
              <button key={i} type="button" disabled={building}
                aria-expanded={def.source === 'industry' ? undefined : isActive}
                aria-haspopup={def.source === 'industry' ? 'dialog' : undefined}
                title={def.question}
                onClick={() => {
                  if (def.source === 'industry') { onOpenBusiness(); return }
                  setActive(p.slot)
                  setOwn('')
                }}
                style={slotStyle(isActive)}>
                {valueOf(p.slot)}
              </button>
            )
          })}
        </p>

        <div style={tray}>
          <span style={{ fontSize: '0.8825rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
            {activeDef?.question}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {activeDef?.source === 'idea'
              ? [idea, ...relatedIdeas].map((r) => (
                <button key={r.key} type="button" aria-pressed={r.key === idea.key} disabled={building}
                  onClick={() => onChangeIdea(r)} style={optStyle(r.key === idea.key)}>{r.title}</button>
              ))
              : (activeDef?.options || []).map((o) => (
                <button key={o} type="button" aria-pressed={values[active] === o} disabled={building}
                  onClick={() => choose(o)} style={optStyle(values[active] === o)}>{o}</button>
              ))}
            {!activeDef?.source && (
              <>
                {values[active] && !(activeDef?.options || []).includes(values[active]) && (
                  <span style={optStyle(true)}><Check size={12} /> {values[active]}</span>
                )}
                <input value={own} onChange={(e) => setOwn(e.target.value)} disabled={building}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyOwn() } }}
                  onBlur={applyOwn}
                  aria-label="Your own answer" placeholder="Something else…" style={ownInput} />
              </>
            )}
          </div>
        </div>

        <div style={extraBox}>
          <div>
            <label htmlFor="blanks-extra" style={{ fontWeight: 600, fontSize: '1.0125rem', color: 'var(--color-text)', margin: 0 }}>
              Anything else? <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>(optional)</span>
            </label>
            <p id="blanks-extra-help" style={{ margin: '2px 0 0', fontSize: '0.9025rem', color: 'var(--color-text-secondary)' }}>
              Rules, exceptions, people, reports — anything the sentence doesn't cover. Type it, or tap a suggestion.
            </p>
          </div>
          {quickAdds.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {quickAdds.map((qa) => {
                const on = extra.includes(qa.text)
                return (
                  <button key={qa.text} type="button" aria-pressed={on} disabled={building}
                    onClick={() => toggleQuick(qa.text)} style={quickChip(on)}>
                    {on ? <Check size={12} /> : <Plus size={12} />}{ucFirst(qa.label)}
                  </button>
                )
              })}
            </div>
          )}
          <textarea id="blanks-extra" aria-describedby="blanks-extra-help" rows={3} value={extra} disabled={building}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="e.g. Urgent requests can skip the manager. Each branch has its own team."
            style={textarea} />
        </div>

        <div style={footer}>
          <button type="button" className="btn btn-ghost" disabled={building} onClick={() => onFreeText(fullText)}>
            <Pencil size={15} /> Edit as free text
          </button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary" disabled={building}
              onClick={() => onQuestions(sentence, extra.trim())}>
              Ask me questions instead
            </button>
            <button type="button" className="btn btn-primary" disabled={building} onClick={() => onBuild(fullText)}>
              {building ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
              {building ? 'Setting up…' : 'Build it'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── styles ────────────────────────────────────────────────────────────
const backLink: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', background: 'none', border: 0,
  padding: '6px 0', fontSize: '0.9425rem', color: 'var(--color-text-secondary)', cursor: 'pointer',
}
const eyebrow: CSSProperties = {
  display: 'inline-flex', fontSize: '0.8625rem', fontWeight: 500, color: 'var(--color-accent)',
  background: 'var(--color-accent-weak)', padding: '4px 10px', borderRadius: 999,
}
const cardBox: CSSProperties = {
  background: 'var(--color-bg)', border: '1px solid var(--color-border-strong)', borderRadius: 14,
  boxShadow: '0 8px 24px var(--color-shadow, rgba(0,0,0,.06))', overflow: 'hidden',
}
const slotStyle = (on: boolean): CSSProperties => ({
  font: 'inherit', display: 'inline', padding: '2px 8px', margin: '0 1px', border: 0, borderRadius: 6, cursor: 'pointer',
  background: on ? 'var(--color-accent)' : 'var(--color-accent-weak)',
  color: on ? '#fff' : 'var(--color-accent)',
  boxShadow: on ? 'none' : 'inset 0 -2px 0 var(--color-accent)',
})
const tray: CSSProperties = {
  margin: '0 18px 18px', padding: 12, borderRadius: 10, background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 8,
}
const optStyle = (on: boolean): CSSProperties => ({
  font: 'inherit', fontSize: '0.9225rem', display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 34,
  padding: '5px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
  background: on ? 'var(--color-accent-weak)' : 'var(--color-bg)',
  color: on ? 'var(--color-accent)' : 'var(--color-text)', fontWeight: on ? 500 : 400,
})
const ownInput: CSSProperties = {
  minHeight: 34, width: 170, padding: '0 10px', border: '1px dashed var(--color-border-strong)', borderRadius: 8,
  background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.9225rem', outline: 'none',
}
const extraBox: CSSProperties = {
  margin: '0 18px 18px', paddingTop: 16, borderTop: '1px solid var(--color-border)',
  display: 'flex', flexDirection: 'column', gap: 10,
}
const quickChip = (on: boolean): CSSProperties => ({
  font: 'inherit', fontSize: '0.8825rem', display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 32,
  padding: '4px 11px', borderRadius: 999, cursor: 'pointer', textAlign: 'left',
  border: `1px solid ${on ? 'var(--color-success)' : 'var(--color-border-strong)'}`,
  background: on ? 'var(--color-success-weak)' : 'var(--color-bg)',
  color: on ? 'var(--color-success)' : 'var(--color-text)',
})
const textarea: CSSProperties = {
  width: '100%', boxSizing: 'border-box', minHeight: 84, padding: '10px 12px', resize: 'vertical',
  border: '1px solid var(--color-border-strong)', borderRadius: 10, background: 'var(--color-input-bg, var(--color-bg))',
  color: 'var(--color-text)', font: 'inherit', fontSize: '1.0125rem', lineHeight: 1.5,
}
const footer: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
  padding: '12px 18px 14px', borderTop: '1px solid var(--color-border)',
}
